import { Directive, ElementRef, EventEmitter, HostListener, Input, NgZone, OnInit, Output } from '@angular/core';
import { fromEvent, interval, merge, Subscription } from 'rxjs';
import { filter, first, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { DelayedUserInputService } from '../services/delayed-user-input.service';

interface Point {
    x: number;
    y: number;
}

interface TopLeft {
    top: number;
    left: number;
}

const MOUSE_PRIMARY = 0;

const HALF_CIRCLE = 180;

@Directive({
    selector: '[circularSliderHandle]',
})
export class CircularSliderHandleDirective implements OnInit {

    @Input()
    get angle(): number {
        return this.angl;
    }

    set angle(deg: number) {
        this.angl = deg || 0;
        this.setTransform(this.calcHandlePosition(this.angl));
        this.angleChange.emit(this.angl);
    }

    @Input() scrollOffsetTop = 0;
    @Input() scrollOffsetLeft = 0;

    @Input()
    diameter?: number;

    @Input() angleMin = 0;
    @Input() angleMax = 360;
    @Input() handleDiameter = 50;
    @Input() circleRadius = 100;

    @Input() cx = 0;
    @Input() cy = 0;

    @Input() leftToRight = false;
    @Input() rightToLeft = false;

    @Input() handleOriginTransformX = 0;
    @Input() handleOriginTransformY = 0;

    @Input() handleName = 'default';

    @Input() enabled: boolean;

    @Output() angleChange = new EventEmitter<number>();

    @Output() movingStart = new EventEmitter();
    @Output() movingEnd = new EventEmitter();

    private handlePressedAndMoveNotFinished = false;
    private movingEndEventFired = false;

    private readonly element: HTMLElement;
    private elementStartSubscription?: Subscription;
    private elementMoveSubscription?: Subscription;
    private angl = 0;
    private windowStart?: Point;
    private layerLatestDelta?: Point;

    constructor(
        private ngZone: NgZone,
        private elementRef: ElementRef,
        private delayedUserInput: DelayedUserInputService,
    ) {
        this.element = this.elementRef.nativeElement;

        this.delayedUserInput.userInputFinished.subscribe(() => {
            this.movingEnd.emit();
            this.movingEndEventFired = true;
        });
    }

    @HostListener('mousedown', ['$event'])
    onClick(event: any): void {
        if (!this.enabled) {
            event.stopPropagation();
        }
    }

    @HostListener('touchstart', ['$event'])
    onTouch(event: any): void {
        if (!this.enabled) {
            event.stopPropagation();
        }
    }

    private getOffset(el: any): TopLeft {
        if (el && el.getBoundingClientRect) {
            const r = el.getBoundingClientRect();
            return {
                top: Math.round(r.top - this.circleRadius - this.handleDiameter / 2 + this.handleOriginTransformY),
                left: Math.round(r.left - this.circleRadius - this.handleDiameter / 2 + this.handleOriginTransformX),
            };
        }
        return {top: 0, left: 0};
    }

    ngOnInit() {
        this.positionHandle();
        if (this.enabled) {
            this.registerStart(this.element);
        }
    }

    private positionHandle(): void {
        this.element.style.boxShadow = 'inset 0 3px 6px rgba(0,0,0,0.16), 0 4px 6px rgba(0,0,0,0.45)';
        this.element.style.width = `${this.handleDiameter}px`;
        this.element.style.height = `${this.handleDiameter}px`;
        this.element.style.top = `${this.cx - this.handleDiameter / 2}px`;
        this.element.style.left = `${this.cy - this.handleDiameter / 2}px`;

        this.setTransform(this.calcHandlePosition(this.angle));
    }

    private registerStart(element: HTMLElement): void {
        this.unregisterStart();
        this.ngZone.runOutsideAngular(() => {
            const mousedown$ = fromEvent<MouseEvent>(element, 'mousedown', {passive: false}).pipe(
                filter(event => event.button === MOUSE_PRIMARY),
                tap(event => {
                    event.preventDefault();
                    event.stopPropagation();
                }),
                map(event => this.parseMouseEvent(event)),
            );
            const touchstart$ = fromEvent<TouchEvent>(element, 'touchstart', {passive: false}).pipe(
                tap(event => {
                    // avoid triggering other draggable parents
                    event.stopPropagation();
                    event.preventDefault();
                }),
                // delay touch
                switchMap(event => interval(1).pipe(
                    first(),
                    takeUntil(fromEvent(document, 'touchend', {passive: false})),
                    map(() => event),
                )),
                map(event => this.parseTouchEvent(event)),
            );
            this.elementStartSubscription = merge(mousedown$, touchstart$).subscribe(windowPoint => {
                this.dragStart(windowPoint);
            });
        });
    }

    private unregisterStart(): void {
        if (this.elementStartSubscription) {
            this.elementStartSubscription.unsubscribe();
            this.elementStartSubscription = undefined;
        }
    }

    private registerMove(): void {
        this.unregisterMove();
        this.elementMoveSubscription = new Subscription();

        const mousemove$ = fromEvent<MouseEvent>(document, 'mousemove', {passive: false}).pipe(
            tap(event => {
                event.preventDefault();
            }),
            map(event => this.parseMouseEvent(event)),
        );
        const touchmove$ = fromEvent<TouchEvent>(document, 'touchmove', {passive: false}).pipe(
            tap(event => {
                if (event.changedTouches && event.changedTouches.length > 1) {
                    event.preventDefault();
                }

                this.delayedUserInput.fireNextUserInput(this.handleName);
            }),
            map(event => this.parseTouchEvent(event)),
        );
        this.elementMoveSubscription.add(
            merge(mousemove$, touchmove$).subscribe(windowPoint => {
                this.dragMove(windowPoint);
                this.delayedUserInput.fireNextUserInput(this.handleName);
            }),
        );

        const mouseup$ = fromEvent<MouseEvent>(document, 'mouseup', {passive: false}).pipe(
            tap(event => {
                event.preventDefault();
                event.stopPropagation();
            }),
        );

        const touchend$ = fromEvent<void>(document, 'touchend', {passive: false});

        this.elementMoveSubscription.add(
            merge(mouseup$, touchend$).subscribe(() => {
                this.dragStop();
            }),
        );
    }

    private unregisterMove(): void {
        if (this.elementMoveSubscription) {
            this.elementMoveSubscription.unsubscribe();
            this.elementMoveSubscription = undefined;
        }
    }

    private dragStart(windowPoint: Point): void {
        if (!this.enabled) {
            return;
        }
        this.handlePressedAndMoveNotFinished = true;
        this.windowStart = windowPoint;
        this.registerMove();
        this.movingStart.emit();
        this.movingEndEventFired = false;
    }

    private dragMove(windowPoint: Point): void {
        const angle = this.calcAngle(windowPoint);
        this.layerLatestDelta = this.calcHandlePosition(angle);

        if (this.handlePressedAndMoveNotFinished && this.movingEndEventFired) {
            this.movingStart.emit();
            this.movingEndEventFired = false;
        }

        this.setTransform(this.layerLatestDelta);
        this.ngZone.run(() => {
            this.angle = Math.round(angle);
        });
    }

    private calcAngle(windowPoint: Point): number {
        let angle = 0;
        if (this.diameter) {
            const offset = -this.circleRadius - this.handleDiameter / 2;
            const mousePos: Point = {
                x: (windowPoint.x + offset),
                y: (windowPoint.y + offset),
            };
            const aTan = Math.atan2(mousePos.x, mousePos.y);
            angle = -aTan / (Math.PI / HALF_CIRCLE) + HALF_CIRCLE;
        }
        angle = this.adjustAngle(angle, -this.calcHandleLeftRightOffsetInDegrees());
        return this.limitAngleToFitInRange(angle);
    }

    private calcHandlePosition(angle: number): Point {
        angle = this.adjustAngle(angle, this.calcHandleLeftRightOffsetInDegrees());
        const newX = this.circleRadius * Math.sin(angle * Math.PI / HALF_CIRCLE);
        const newY = this.circleRadius * -Math.cos(angle * Math.PI / HALF_CIRCLE);

        return {
            x: newX,
            y: newY,
        };
    }

    private dragStop(): void {
        this.handlePressedAndMoveNotFinished = false;
        this.unregisterMove();
        if (!this.layerLatestDelta) {
            return;
        }

        this.ngZone.run(() => {
            this.layerLatestDelta = undefined;
        });
    }

    private setTransform(point: Point): void {
        this.element.style.transform = `translate(${point.x}px, ${point.y}px)`;
    }

    private parseMouseEvent(event: MouseEvent): Point {
        const o = this.getOffset(this.element.offsetParent);

        return {
            x: event.pageX - o.left,
            y: event.pageY - o.top,
        };
    }

    private parseTouchEvent(event: TouchEvent): Point {
        const touch = event.touches[0];
        const o = this.getOffset(this.element.offsetParent);

        return {
            x: touch.pageX - o.left,
            y: touch.pageY - o.top,
        };
    }

    private limitAngleToFitInRange(angle: number): number {
        const rotMinAngle = 0;
        const rotMaxAngle = (this.angleMin <= this.angleMax) ? (this.angleMax - this.angleMin) : (360 - this.angleMin + this.angleMax);
        let rotAngle;

        if (this.angleMin <= this.angleMax) {
            rotAngle = angle - this.angleMin;
        } else if (angle >= this.angleMin) {
            rotAngle = angle - this.angleMin;
        } else {
            rotAngle = angle - this.angleMin + 360;
        }

        if (rotMinAngle <= rotAngle && rotAngle < rotMaxAngle) {
            return angle;
        }
        const midAngle = rotMaxAngle / 2;

        if (rotAngle > rotMaxAngle && rotAngle < (midAngle + HALF_CIRCLE)) {
            return this.angleMax;
        }
        if (rotAngle > rotMaxAngle && rotAngle > (midAngle - HALF_CIRCLE)) {
            return this.angleMin;
        }

        if (rotAngle < rotMinAngle && rotAngle > (rotMinAngle - HALF_CIRCLE + midAngle)) {
            return this.angleMin;
        }
        return this.angleMax;
    }

    private adjustAngle(angle: number, offset: number): number {
        if (this.leftToRight) {
            return angle - offset;
        }
        if (this.rightToLeft) {
            return angle + offset;
        }
        return angle;
    }

    private calcHandleLeftRightOffsetInDegrees(): number {
        const handleQuarter = (this.handleDiameter / 4);
        return (HALF_CIRCLE * handleQuarter) / (Math.PI * this.circleRadius);
    }
}
